/**
 * Iter-52.54 — Standalone Answer Key screen.
 *
 * Purpose: keep the answer key OFF the Exercises screen so the child
 * has to make a deliberate action to open it. Adds anti-copy-paste
 * measures for the target demographic where students screenshot / copy
 * answers into notebooks without ever reading them:
 *
 *   • `user-select: none` on the whole screen
 *   • Blocks the native copy event (onCopy → preventDefault)
 *   • Blocks the right-click / long-press context menu
 *
 * UX:
 *   • Sticky "← Back to exercises / वापस अभ्यास पर" bar at top
 *   • Grouped by the textbook's own printed sections + instructions
 *   • Textbook-native item labels ("2. a)", "5. c) (ii)"), NEVER
 *     invented numbering
 *   • Each answer row: item_label → question (with meaning) → answer
 *     (with meaning). If an answer is still missing after fetch + one
 *     retry, we surface a compact "Answer not available — tap to
 *     retry / दोबारा कोशिश करें" affordance.
 *   • 🔊 karaoke on every question + answer, powered by the same
 *     TappableText/useTts pipeline used elsewhere.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, KeyRound, RotateCcw } from 'lucide-react';
import { api, API } from '../lib/api';
import { useApp } from '../context/AppContext';
import { stripMarkers, parseMatchExercise, isPictureItem } from '../lib/textMarkers';
import { answerTextFor as _answerTextFor } from '../lib/answerKeyLabels';
import useTts from '../lib/useTts';
import TappableText from '../components/TappableText';
import SyllablePopover from '../components/SyllablePopover';
import { groupExercisesBySection } from '../components/ExercisesPanel';

const isDevanagari = (s) => /[\u0900-\u097F]/.test(s || '');

// Iter-52.55 — Mirror the URL helper from ExercisesPanel so the answer
// key can render backend-hosted crop URLs (`/api/uploads/match/...`).
const backendUrl = (p) => {
  if (!p) return '';
  const s = String(p);
  if (/^(https?:|data:)/i.test(s)) return s;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${s.startsWith('/') ? '' : '/'}${s}`;
};

/** Speaker button — mirrored from ExercisesPanel so we don't couple
 *  Answer Key rendering to the panel's internal component tree. */
function SpeakerBtn({ text, voiceGender, tts, id, onPlayStart, onWordChange, activePlaybackId }) {
  if (!text || !text.trim()) return null;
  const isThisPlaying = activePlaybackId === id;
  const busy = tts.loading && isThisPlaying;
  const playing = tts.playing && isThisPlaying;
  return (
    <button type="button"
      data-testid={`answer-key-speaker-${id}`}
      onClick={(e) => {
        e.stopPropagation();
        if (isThisPlaying) { tts.stop(); onWordChange && onWordChange(-1); return; }
        tts.stop();
        onPlayStart && onPlayStart(id);
        const lang = isDevanagari(text) ? 'hi' : 'en';
        tts.play(text, {
          language: lang, speed: 0.9, voice_gender: voiceGender || 'female',
          withTimestamps: true,
          onWordChange: (idx) => { onWordChange && onWordChange(idx, id); },
        });
      }}
      className={`inline-flex items-center justify-center rounded-full w-6 h-6 border shrink-0 ${busy || playing ? 'bg-[#FF6B1A] text-white border-[#FF6B1A]' : 'bg-white text-emerald-700 border-emerald-500/40 hover:border-emerald-600'}`}
      aria-label="Read aloud"
    >
      {busy ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>
       : playing ? '⏸' : '🔊'}
    </button>
  );
}

function SpokenLine({ text, speechText, id, className, activePlaybackId, activeWordIdx, tts, voiceGender, onPlayStart, onWordChange, onWordTap }) {
  if (!text || !text.trim()) return null;
  const isThisActive = activePlaybackId === id;
  // Iter-52.55.7 — the display text may contain "→" (U+2192) which
  // ElevenLabs skips silently, so an answer like "1 → C. Sentence"
  // reads as "1 C Sentence" — confusing for a child. Callers pass a
  // TTS-friendly `speechText` (e.g. "1 matches C. Sentence") when the
  // display form contains such symbols.
  const spoken = (speechText || text)
    .replace(/\s*→\s*/g, ' matches ')
    .replace(/\s*\[picture\s*\d+\]\s*/gi, '');
  return (
    <div className="flex items-start gap-2">
      <SpeakerBtn id={id} text={spoken} tts={tts} voiceGender={voiceGender}
        activePlaybackId={activePlaybackId}
        onPlayStart={onPlayStart} onWordChange={onWordChange}/>
      <TappableText
        text={text}
        className={`flex-1 min-w-0 break-words leading-snug ${className || ''}`}
        style={{ textAlign: 'left', hyphens: 'auto' }}
        activeWordIdx={isThisActive ? activeWordIdx : -1}
        onWordTap={(w) => onWordTap && onWordTap(w, text)}
      />
    </div>
  );
}

export function answerTextFor(q) {
  // Iter-52.55.7 — thin re-export so existing imports keep working;
  // canonical implementation lives in `lib/answerKeyLabels.js`.
  return _answerTextFor(q);
}

export default function AnswerKeyScreen() {
  const { bookId, pageId } = useParams();
  const nav = useNavigate();
  const { activeChild } = useApp();
  const [book, setBook] = useState(null);
  const [bookErrored, setBookErrored] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [activePlaybackId, setActivePlaybackId] = useState(null);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  const [popoverWord, setPopoverWord] = useState(null);
  const [retryTicks, setRetryTicks] = useState({}); // per-question manual retry counter
  const activePlaybackIdRef = useRef(null);
  const retriedRef = useRef(false);
  const tts = useTts(0.9);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}`);
        if (!cancelled) setBook(data);
      } catch {
        if (!cancelled) setBookErrored(true);
      }
    })();
    return () => { cancelled = true; };
  }, [bookId]);

  const page = book?.pages?.find(p => p.page_id === pageId) || null;
  const rawText = page?.text || '';
  const parsedMatch = parseMatchExercise(rawText);
  const matchBlockPayload = parsedMatch.isMatch ? {
    instruction: parsedMatch.instruction,
    left: parsedMatch.left,
    right: parsedMatch.right,
    left_images: parsedMatch.leftImages || [],
  } : null;
  const cleanPassage = stripMarkers(rawText).replace(/^##\s*/gm, '').trim();
  const gender = activeChild?.voice_gender || 'female';
  const language = (book?.language || 'en').toLowerCase().startsWith('hi') ? 'hi' : 'en';
  const hiFont = language === 'hi' ? 'font-hindi' : '';

  const passageKey = cleanPassage.slice(0, 120);

  // Iter-52.55.3 — poll-based detect (see ExercisesPanel for the
  // full rationale).
  const detectPoll = async () => {
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 20000);
    try {
      const resp = await fetch(`${API}/exercises/detect`, {
        method: 'POST', credentials: 'include', signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passage: cleanPassage || '',
          language,
          cache_key: `${bookId}:${pageId}:${language}`,
          match_block: matchBlockPayload || undefined,
          book_id: bookId,
          page_id: pageId,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } finally { clearTimeout(to); }
  };

  const load = async () => {
    if (!page || (cleanPassage.length < 60 && !matchBlockPayload)) { setLoading(false); return; }
    setLoading(true); setErrored(false);
    const deadline = Date.now() + 180000;
    let seenText = false;
    let failCount = 0;
    try {
      while (Date.now() < deadline) {
        let data;
        try { data = await detectPoll(); }
        catch {
          failCount += 1;
          if (failCount >= 3) { setErrored(true); return; }
          await new Promise(r => setTimeout(r, 3000));
          continue;
        }
        failCount = 0;
        const items = Array.isArray(data.exercises)
          ? data.exercises.filter(q => q && q.question && q.question.trim())
          : [];
        if (items.length > 0) {
          setExercises(items);
          seenText = true;
          setLoading(false);
        }
        const st = data.status;
        if (st === 'done') return;
        if (st === 'failed') { if (!seenText) setErrored(true); return; }
        await new Promise(r => setTimeout(r, 3000));
      }
      if (!seenText) setErrored(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.page_id, passageKey]);

  const sections = useMemo(() => groupExercisesBySection(exercises), [exercises]);

  const handlePlayStart = (id) => {
    activePlaybackIdRef.current = id;
    setActivePlaybackId(id);
    setActiveWordIdx(-1);
  };
  const handleWordChange = (idx, id) => {
    if (id && id !== activePlaybackIdRef.current && idx !== -1) return;
    setActiveWordIdx(idx);
    if (idx === -1) {
      activePlaybackIdRef.current = null;
      setActivePlaybackId(null);
    }
  };
  const handleWordTap = (word, sentence) => {
    const clean = String(word || '').replace(/[^\p{L}\p{N}\-']/gu, '');
    if (!clean) return;
    setPopoverWord({ word: clean, language: isDevanagari(sentence) ? 'hi' : 'en', sentence });
  };

  // Per-question single-retry: forces a fresh detect run keyed under a
  // unique cache suffix so the LLM re-produces missing answers.
  const retryQuestion = (qid) => {
    setRetryTicks(prev => ({ ...prev, [qid]: (prev[qid] || 0) + 1 }));
    retriedRef.current = false;
    load();
  };

  // Anti-copy affordances — block copy + context menu at the DOM level.
  const blockCopy = (e) => { e.preventDefault(); };
  const blockContextMenu = (e) => { e.preventDefault(); };

  return (
    <div
      data-testid="answer-key-screen"
      onCopy={blockCopy}
      onCut={blockCopy}
      onContextMenu={blockContextMenu}
      className={`min-h-screen bg-[#F0FDF4] ${hiFont}`}
      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
    >
      <header className="sticky top-0 z-20 bg-white border-b border-emerald-200/60">
        <div className="max-w-md mx-auto px-3 py-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="answer-key-back-btn"
            onClick={() => nav(`/exercises/${bookId}/${pageId}`)}
            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 hover:text-emerald-900">
            <ChevronLeft size={18} strokeWidth={3}/>
            Back to exercises / वापस अभ्यास पर
          </button>
        </div>
      </header>
      <main className="px-3 pb-24 max-w-md mx-auto">
        <div className="flex items-center gap-2 mt-3 mb-3">
          <KeyRound size={16} strokeWidth={3} className="text-emerald-700"/>
          <h1 className="text-base md:text-lg font-black text-emerald-900">
            Answer Key / उत्तर कुंजी
          </h1>
        </div>
        <p className="text-[11px] italic text-slate-500 mb-3">
          Try it yourself first next time. / अगली बार पहले खुद try करें।
        </p>

        {!book && !bookErrored && (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin"/> Loading…
          </div>
        )}
        {bookErrored && (
          <div className="mt-6 text-sm text-red-500 font-bold">
            Couldn't load this book. Please go back and try again.
          </div>
        )}
        {book && loading && (
          <div className="bg-white rounded-3xl p-4 flex items-start gap-3 text-sm text-slate-600 border border-emerald-300/50">
            <Loader2 size={18} className="animate-spin text-emerald-600 mt-0.5 shrink-0"/>
            <div className="flex-1">
              <p className="font-bold text-emerald-900">Answer key tayyar ho rahi hai…</p>
              <p className="text-xs italic text-slate-500 mt-1">
                Answer key तैयार हो रही है, इसमें 30–60 सेकंड लग सकते हैं। कृपया रुकें।
              </p>
              <p className="text-xs italic text-slate-500 mt-0.5">
                Please wait — this can take 30–60 seconds. 🙏
              </p>
            </div>
          </div>
        )}
        {book && !loading && errored && (
          <button
            data-testid="answer-key-retry"
            onClick={() => { retriedRef.current = false; load(); }}
            className="w-full bg-white rounded-3xl p-4 text-left font-bold text-[#B85582] border border-[#B85582]/30 hover:bg-[#B85582]/5">
            <span className="block text-sm">Thoda zyada time lag raha hai — kripya page refresh karein.</span>
            <span className="block text-xs italic text-slate-500 mt-1">कृपया पेज रीफ़्रेश करें।</span>
            <span className="block text-xs italic text-slate-500 mt-0.5">Taking longer than usual — tap to retry.</span>
          </button>
        )}
        {book && !loading && !errored && sections.length === 0 && (
          <div className="bg-white rounded-3xl p-4 text-sm text-slate-500">
            No exercises detected on this page.
          </div>
        )}

        {sections.map(section => (
          <section key={section.section_key}
            data-testid={`answer-key-section-${section.section_key}`}
            className="mt-4 first:mt-0">
            {section.section_heading && (
              <div className="mb-2">
                <SpokenLine
                  id={`ak-sec-h-${section.section_key}`}
                  text={section.section_heading}
                  activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                  tts={tts} voiceGender={gender}
                  onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                  className="text-base md:text-lg font-black text-emerald-900 uppercase tracking-wide"/>
                {section.section_heading_meaning && (
                  <div className="ml-8 mt-0.5">
                    <SpokenLine
                      id={`ak-sec-hm-${section.section_key}`}
                      text={section.section_heading_meaning}
                      activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                      tts={tts} voiceGender={gender}
                      onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                      className="text-xs md:text-sm italic text-slate-500"/>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              {section.items.map(q => {
                const ansText = answerTextFor(q);
                const label = (q.item_label || '').trim();
                const missing = !ansText.trim();
                // Iter-52.55 — highlight the correct picture option(s)
                // as small thumbnails so the child can visually match
                // the letter (A/B/C) to the picture from the exercise.
                const picOptions = (q.type === 'mcq' && q.options_are_pictures && Array.isArray(q.options_images))
                  ? (Array.isArray(q.answer_indices)
                      ? q.answer_indices
                      : (typeof q.answer_index === 'number' && q.answer_index >= 0 ? [q.answer_index] : []))
                    .map(idx => {
                      const useTextbookLabels = Array.isArray(q.option_labels) && q.option_labels.length === (q.options || []).length;
                      return {
                        idx,
                        letter: useTextbookLabels ? q.option_labels[idx] : String.fromCharCode(65 + idx),
                        caption: (q.options || [])[idx] || '',
                        url: backendUrl((q.options_images || [])[idx] || ''),
                      };
                    })
                    .filter(o => o.url)
                  : [];
                return (
                  <div key={q.id} data-testid={`answer-key-row-${q.id}`}
                    className="rounded-xl bg-white border border-emerald-200/70 p-2 md:p-3">
                    <div className="flex items-start gap-2">
                      {label && (
                        <span className="mt-1 text-[11px] md:text-xs font-black text-emerald-800 shrink-0 min-w-[1.75rem]">
                          {label}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <SpokenLine
                          id={`ak-q-${q.id}`}
                          text={q.question}
                          activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                          tts={tts} voiceGender={gender}
                          onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                          className="text-sm md:text-base font-bold text-[#2B2140]"/>
                        {q.question_meaning && (
                          <div className="mt-1">
                            <SpokenLine
                              id={`ak-qm-${q.id}`}
                              text={q.question_meaning}
                              activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                              tts={tts} voiceGender={gender}
                              onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                              className="text-xs md:text-sm italic text-slate-500"/>
                          </div>
                        )}
                        {q.question_image && (
                          <img src={backendUrl(q.question_image)} alt={q.question || 'referenced'}
                            data-testid={`answer-key-question-image-${q.id}`}
                            className="mt-1 max-h-40 w-auto max-w-full object-contain rounded border border-emerald-200 bg-white"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-start gap-2 pl-1">
                      <span className="text-[10px] font-black text-emerald-700 mt-1 min-w-[2.5rem]">Ans.</span>
                      <div className="flex-1 min-w-0">
                        {missing ? (
                          <button type="button"
                            data-testid={`answer-key-retry-${q.id}`}
                            onClick={() => retryQuestion(q.id)}
                            className="inline-flex items-center gap-1.5 text-xs md:text-sm font-bold text-[#B85582] hover:text-[#8E3A63]">
                            <RotateCcw size={12} strokeWidth={2.5}/>
                            Answer not available — tap to retry / दोबारा कोशिश करें
                          </button>
                        ) : (
                          <SpokenLine
                            id={`ak-a-${q.id}-${retryTicks[q.id] || 0}`}
                            text={ansText}
                            activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                            tts={tts} voiceGender={gender}
                            onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                            className="text-sm md:text-base font-bold text-emerald-900 whitespace-pre-wrap"/>
                        )}
                        {picOptions.length > 0 && (
                          <div data-testid={`answer-key-picture-thumbs-${q.id}`}
                            className="mt-2 flex flex-wrap gap-2">
                            {picOptions.map(p => (
                              <figure key={p.idx} className="flex flex-col items-center gap-0.5 border border-emerald-300 rounded p-1 bg-emerald-50">
                                <img src={p.url} alt={p.caption}
                                  className="h-16 w-16 object-contain rounded bg-white"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                                <figcaption className="text-[10px] font-black text-emerald-800">
                                  {p.letter}. {p.caption}
                                </figcaption>
                              </figure>
                            ))}
                          </div>
                        )}
                        {q.type === 'match_the_following' && Array.isArray(q.answer_pairs) && !missing && (
                          <div data-testid={`answer-key-match-picture-rows-${q.id}`}
                            className="mt-2 space-y-1.5">
                            {q.answer_pairs.map((rIdx, lIdx) => {
                              const leftText = q.left?.[lIdx] || '';
                              const leftImg = q.left_images?.[lIdx] || '';
                              const rightText = q.right?.[rIdx] || '';
                              if (!isPictureItem(leftText, leftImg)) return null;
                              // Iter-52.55.7 — labels mirror the exercise
                              // page exactly (Column A = 1..N, Column B
                              // = A..Z). Never mix numeric/roman/lowercase.
                              const leftLabel = String(lIdx + 1);
                              const rightLabel = String.fromCharCode(65 + rIdx);
                              return (
                                <div key={lIdx} data-testid={`answer-key-match-row-${q.id}-${lIdx}`}
                                  className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded p-1.5">
                                  <span className="text-[10px] font-black text-emerald-700 shrink-0">{leftLabel}.</span>
                                  {leftImg ? (
                                    <img src={backendUrl(leftImg)} alt={`Picture ${lIdx + 1}`}
                                      data-testid={`answer-key-match-thumb-${q.id}-${lIdx}`}
                                      className="h-14 w-20 object-contain bg-white rounded border"
                                      onError={(e) => {
                                        // If crop fails, fall back to a friendly bilingual label.
                                        const parent = e.currentTarget.parentElement;
                                        e.currentTarget.style.display = 'none';
                                        const fb = parent.querySelector('[data-picture-fallback]');
                                        if (fb) fb.style.display = 'inline';
                                      }}/>
                                  ) : null}
                                  <span data-picture-fallback
                                    style={{ display: leftImg ? 'none' : 'inline' }}
                                    className="text-[11px] italic text-slate-600">
                                    Picture {lIdx + 1} / चित्र {lIdx + 1}
                                  </span>
                                  <span className="text-emerald-900 font-black">→</span>
                                  <span className="text-[11px] font-black text-emerald-700 shrink-0">{rightLabel}.</span>
                                  <span className="text-xs md:text-sm font-bold text-emerald-900 flex-1 break-words text-left">{rightText}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {q.type === 'match_the_following' && q.has_rewrite && q.followup_instruction && (
                          <div className="mt-2 rounded-lg border border-emerald-300 bg-emerald-50/60 p-2"
                            data-testid={`answer-key-match-rewrite-${q.id}`}>
                            <SpokenLine
                              id={`ak-rw-instr-${q.id}`}
                              text={q.followup_instruction}
                              activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                              tts={tts} voiceGender={gender}
                              onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                              className="text-xs md:text-sm font-black text-emerald-900"/>
                          </div>
                        )}
                        {q.answer_meaning && !missing && (
                          <div className="mt-1">
                            <SpokenLine
                              id={`ak-am-${q.id}`}
                              text={q.answer_meaning}
                              activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                              tts={tts} voiceGender={gender}
                              onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                              className="text-xs md:text-sm italic text-slate-500"/>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      {popoverWord && (
        <SyllablePopover
          word={popoverWord.word}
          language={popoverWord.language}
          sentence={popoverWord.sentence}
          wordIndex={0}
          ttsPlay={tts.play}
          voiceGender={gender}
          onClose={() => setPopoverWord(null)}
        />
      )}
    </div>
  );
}
